import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { useUi } from '@/store/uiStore';
import { BOOK_NAMES } from '@/lib/books';
import { formatRef } from '@/lib/utils';

export function NoteDialog() {
  const id = useUi((s) => s.noteDialogId);
  const close = useUi((s) => s.closeNoteDialog);
  const showToast = useUi((s) => s.showToast);

  const open = id !== null;
  const note = useLiveQuery(
    async () => (id ? await db.notes.get(id) : undefined),
    [id],
  );

  // Body is hydrated once from the note, then the textarea owns it.
  const [body, setBody] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset state when dialog opens/closes for a different note
  useEffect(() => {
    if (!open) {
      setBody(null);
      setSavedAt(null);
    }
  }, [open, id]);

  // Hydrate once
  useEffect(() => {
    if (note && body === null) setBody(note.body);
  }, [note, body]);

  // Autosave 600ms after typing pauses
  useEffect(() => {
    if (!note || body === null) return;
    if (body === note.body) return;
    const h = setTimeout(() => {
      void db.notes
        .put({ ...note, body, updatedAt: Date.now() })
        .then(() => setSavedAt(Date.now()));
    }, 600);
    return () => clearTimeout(h);
  }, [body, note]);

  // Autofocus the textarea when the dialog opens
  useEffect(() => {
    if (!open || body === null) return;
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open, body]);

  const onDelete = async () => {
    if (!note) return;
    if (!confirm('Delete this note?')) return;
    await db.notes.delete(note.id);
    showToast('Deleted');
    close();
  };

  const onClose = (next: boolean) => {
    if (next) return;
    // If the user closes while a save is pending, flush it synchronously
    if (note && body !== null && body !== note.body) {
      void db.notes.put({ ...note, body, updatedAt: Date.now() });
    }
    close();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className="dialog-content"
          aria-describedby={undefined}
          style={{ maxWidth: 'min(640px, 96vw)' }}
        >
          <header style={{ display: 'flex', alignItems: 'baseline', gap: '.75rem', marginBottom: '.75rem' }}>
            <Dialog.Title style={{ margin: 0, fontSize: '1.05rem' }}>
              {note
                ? formatRef(note.ref, (bid) => BOOK_NAMES[bid as keyof typeof BOOK_NAMES] ?? bid)
                : 'Note'}
            </Dialog.Title>
            <span className="faint" style={{ marginInlineStart: 'auto', fontSize: '.8rem', fontFamily: 'var(--font-sans)' }}>
              {!note
                ? ''
                : body !== null && body !== note.body
                  ? 'editing…'
                  : savedAt
                    ? `saved · ${new Date(savedAt).toLocaleTimeString()}`
                    : new Date(note.updatedAt).toLocaleDateString()}
            </span>
          </header>

          {!note || body === null ? (
            <p className="muted">…</p>
          ) : (
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Write your thoughts on this verse…"
              aria-label="Note body"
              style={{
                width: '100%',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: '.75rem',
                fontFamily: 'var(--font-serif)',
                fontSize: '1rem',
                color: 'var(--color-text)',
                lineHeight: 1.55,
                resize: 'vertical',
                minHeight: '40dvh',
              }}
            />
          )}

          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'space-between', marginTop: '1rem' }}>
            <button
              className="btn-secondary"
              onClick={() => void onDelete()}
              style={{ color: 'var(--color-danger)' }}
            >
              Delete
            </button>
            <Dialog.Close asChild>
              <button className="btn-primary">Done</button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
